import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Eye, RefreshCw, Trash2, TrendingUp, Users, Calendar, CalendarDays } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatTimestamp = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    return d.toLocaleString('az-AZ', {
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
};

const StatCard = ({ icon: Icon, label, value, color }) => (
  <div
    style={{
      background: `${color}11`,
      border: `1px solid ${color}33`,
      borderRadius: 10,
      padding: '12px 14px',
      display: 'flex',
      alignItems: 'center',
      gap: 12,
      minWidth: 0,
    }}
    data-testid={`visit-stat-${label.toLowerCase().replace(/\s+/g, '-')}`}
  >
    <div
      style={{
        width: 36,
        height: 36,
        borderRadius: '50%',
        background: `${color}22`,
        color,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      <Icon size={18} />
    </div>
    <div style={{ minWidth: 0 }}>
      <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: 0.5 }}>
        {label}
      </div>
      <div style={{ fontSize: 20, fontWeight: 700, color: '#fff', fontFamily: 'Inter, sans-serif' }}>
        {value.toLocaleString()}
      </div>
    </div>
  </div>
);

const SiteVisitsViewer = ({ token }) => {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({ total: 0, today: 0, last_7_days: 0, last_30_days: 0, unique_ips_30d: 0 });
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [confirmClear, setConfirmClear] = useState(false);

  const fetchVisits = useCallback(async () => {
    setLoading(true);
    setErr('');
    try {
      const res = await axios.get(`${API}/admin/visits`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { limit: 200 },
      });
      setItems(res.data?.items || []);
      setStats(res.data?.stats || stats);
    } catch (e) {
      setErr('Baxışları gətirmək alınmadı: ' + (e.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    if (token) fetchVisits();
  }, [fetchVisits, token]);

  const handleClear = async () => {
    try {
      await axios.delete(`${API}/admin/visits`, { headers: { Authorization: `Bearer ${token}` } });
      setItems([]);
      setStats({ total: 0, today: 0, last_7_days: 0, last_30_days: 0, unique_ips_30d: 0 });
      setConfirmClear(false);
    } catch (e) {
      setErr('Silmək alınmadı: ' + (e.response?.data?.detail || e.message));
    }
  };

  return (
    <div data-testid="site-visits-viewer" style={{ marginTop: 28 }}>
      <h3 className="admin-section-title flex items-center gap-2">
        <Eye size={16} /> Sayt baxışları
      </h3>
      <p className="admin-section-desc">
        Səhifə yüklənmələri sayğacı və son ziyarətçi loqu. Sistem ən son 5000 baxışı saxlayır.
      </p>

      {/* Stats grid */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
          gap: 10,
          marginBottom: 16,
        }}
      >
        <StatCard icon={TrendingUp} label="Cəmi" value={stats.total} color="#a855f7" />
        <StatCard icon={Calendar} label="Bu gün" value={stats.today} color="#22c55e" />
        <StatCard icon={CalendarDays} label="Son 7 gün" value={stats.last_7_days} color="#3b82f6" />
        <StatCard icon={CalendarDays} label="Son 30 gün" value={stats.last_30_days} color="#06b6d4" />
        <StatCard icon={Users} label="Unikal IP (30g)" value={stats.unique_ips_30d} color="#f59e0b" />
      </div>

      {/* Actions */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
        <button
          type="button"
          onClick={fetchVisits}
          className="admin-btn-secondary"
          disabled={loading}
          data-testid="visits-refresh-btn"
          title="Yenidən yüklə"
        >
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Yenilə
        </button>
        <div style={{ flex: 1 }} />
        {items.length > 0 && !confirmClear && (
          <button
            type="button"
            onClick={() => setConfirmClear(true)}
            className="admin-btn-secondary"
            data-testid="visits-clear-btn"
            style={{ color: '#fca5a5', borderColor: 'rgba(239,68,68,0.3)' }}
          >
            <Trash2 size={14} /> Bütün loqu sil
          </button>
        )}
        {confirmClear && (
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <span style={{ fontSize: 12, color: '#fca5a5' }}>Bütün baxış loqları silinəcək. Əminsiniz?</span>
            <button
              type="button"
              onClick={handleClear}
              className="admin-btn-primary"
              style={{ background: 'rgba(239,68,68,0.2)', borderColor: 'rgba(239,68,68,0.5)', color: '#fca5a5' }}
              data-testid="visits-clear-confirm"
            >
              Bəli, sil
            </button>
            <button
              type="button"
              onClick={() => setConfirmClear(false)}
              className="admin-btn-secondary"
              data-testid="visits-clear-cancel"
            >
              Ləğv
            </button>
          </div>
        )}
      </div>

      {err && (
        <div
          style={{
            background: 'rgba(239,68,68,0.08)',
            border: '1px solid rgba(239,68,68,0.3)',
            color: '#fca5a5',
            padding: '10px 14px',
            borderRadius: 8,
            marginBottom: 12,
            fontSize: 13,
          }}
        >
          {err}
        </div>
      )}

      {/* Table */}
      <div
        style={{
          background: 'rgba(255,255,255,0.02)',
          border: '1px solid rgba(255,255,255,0.06)',
          borderRadius: 10,
          overflow: 'hidden',
        }}
      >
        {items.length === 0 ? (
          <div
            style={{
              padding: '32px 16px',
              textAlign: 'center',
              color: 'rgba(255,255,255,0.4)',
              fontSize: 13,
            }}
            data-testid="visits-empty"
          >
            {loading ? 'Yüklənir...' : 'Hələ heç bir baxış qeydə alınmayıb.'}
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ background: 'rgba(168, 85, 247, 0.08)', color: '#f0e68c', textAlign: 'left' }}>
                  <th style={{ padding: '10px 12px', fontWeight: 600 }}>Vaxt</th>
                  <th style={{ padding: '10px 12px', fontWeight: 600 }}>IP</th>
                  <th style={{ padding: '10px 12px', fontWeight: 600 }}>Mənbə</th>
                  <th style={{ padding: '10px 12px', fontWeight: 600 }}>Cihaz</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it, idx) => (
                  <tr
                    key={idx}
                    style={{ borderTop: '1px solid rgba(255,255,255,0.04)' }}
                    data-testid={`visit-row-${idx}`}
                  >
                    <td style={{ padding: '10px 12px', fontFamily: 'monospace', whiteSpace: 'nowrap', color: 'rgba(255,255,255,0.75)' }}>
                      {formatTimestamp(it.timestamp)}
                    </td>
                    <td style={{ padding: '10px 12px', fontFamily: 'monospace', color: '#e2e8f0', whiteSpace: 'nowrap' }}>
                      {it.ip || '—'}
                    </td>
                    <td
                      style={{
                        padding: '10px 12px',
                        color: 'rgba(255,255,255,0.55)',
                        maxWidth: 220,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                      title={it.referer}
                    >
                      {it.referer || 'birbaşa'}
                    </td>
                    <td
                      style={{
                        padding: '10px 12px',
                        color: 'rgba(255,255,255,0.4)',
                        maxWidth: 240,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                      title={it.user_agent}
                    >
                      {it.user_agent || '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default SiteVisitsViewer;
